// =============================================================================
// card-acquisition-service.ts — pure-function port of Cards/CardAcquisitionService.cs
// No class, no singleton, no module-level state.
//
// Unity: MonoBehaviour singleton with TryAcquireCard(card, seat, src) → AcquireResult
//        + aura-passive registration on hand entry.
//
// Web: pure function over (cisState, auraState, args) → { result, newCis, newAura, log }
//      Callers resolve hand size from their own state.
//
// Function map (Unity → TS):
//   TryAcquireCard(card, seatIndex, src)   → tryAcquireCard(...)
//   TryRegisterAuraPassive(card, seat, id) → tryRegisterAuraPassive(...)
// =============================================================================

import type { CardDef } from "@gdg/shared";
import type { ActionLog } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";
import type { AuraInstance, AuraManagerState, RegisterResult } from "./aura-manager.js";
import { register } from "./aura-manager.js";
import type { CardInstanceState, CreateForSeatResult } from "./card-instance-service.js";
import { createForSeat } from "./card-instance-service.js";

// ---------------------------------------------------------------------------
// AcquireResult enum (ported from CardAcquisitionService.cs AcquireResult)
// ---------------------------------------------------------------------------

/** Return value of tryAcquireCard. Never a bare boolean (Phase 3.1a locked rule). */
export const AcquireResult = {
  /** Card added to hand, CIS instance minted. */
  Acquired: "Acquired",
  /** Hand is full — caller must show swap modal. */
  SwapNeeded: "SwapNeeded",
  /** Rejected due to missing card, missing player, or AddCard failure. Logged. */
  Skipped: "Skipped",
  /** Explicitly cancelled by caller (unused in core; reserved for UI cancel paths). */
  Cancelled: "Cancelled",
} as const;
export type AcquireResult = (typeof AcquireResult)[keyof typeof AcquireResult];

// ---------------------------------------------------------------------------
// AcquireSource enum (ported from CardAcquisitionService.cs AcquireSource)
// ---------------------------------------------------------------------------

export const AcquireSource = {
  Draw: "Draw",
  Buy: "Buy",
  Steal: "Steal",
  Reward: "Reward",
} as const;
export type AcquireSource = (typeof AcquireSource)[keyof typeof AcquireSource];

// ---------------------------------------------------------------------------
// MAX_HAND_SIZE — Unity canonical: BoardPlayer.MAX_HAND_SIZE = 5
// (BoardPlayer.cs:44; CardAcquisitionService.cs:69 reads via BoardPlayer).
// Web port mirrors the constant exactly.
// ---------------------------------------------------------------------------

export const MAX_HAND_SIZE = 5;

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface TryAcquireCardResult {
  readonly result: AcquireResult;
  readonly newCis: CardInstanceState;
  readonly newAura: AuraManagerState;
  readonly lastAcquiredInstanceId: number;
  readonly log: ActionLog;
}

// ---------------------------------------------------------------------------
// tryRegisterAuraPassive
// ---------------------------------------------------------------------------

/**
 * Register Whenever (aura) card effects with AuraManager immediately on hand entry.
 * No-op for non-Whenever cards or cards without AuraApply/TollMod effects.
 *
 * Mirrors CardAcquisitionService.TryRegisterAuraPassive(card, seatIndex, instanceId).
 */
export function tryRegisterAuraPassive(
  auraState: AuraManagerState,
  log: ActionLog,
  card: CardDef,
  seatIndex: number,
  instanceId: number,
): { newAura: AuraManagerState; log: ActionLog } {
  if (card.discardMode !== "Whenever") return { newAura: auraState, log };
  if (card.effects == null || card.effects.length === 0) return { newAura: auraState, log };

  let currentAura = auraState;
  let currentLog = log;

  for (const eff of card.effects) {
    if (eff.kind !== "AuraApply" && eff.kind !== "TollMod") continue;

    // sourceCardName: prefer card.cardName, fall back to card.name (mirrors Unity IsNullOrEmpty)
    const sourceCardName = card.cardName.length > 0 ? card.cardName : card.name;

    const inst: AuraInstance = {
      instanceId,
      ownerSeat: seatIndex,
      sourceCardName,
      effect: eff,
      scope: eff.auraScope ?? "Toll",
      isPassive: true,
      categoryFilter: null,
    };

    const regResult: RegisterResult = register(currentAura, currentLog, inst);
    currentAura = regResult.newState;
    currentLog = regResult.log;
  }

  return { newAura: currentAura, log: currentLog };
}

// ---------------------------------------------------------------------------
// tryAcquireCard
// ---------------------------------------------------------------------------

/**
 * Attempt to give card to seat. Returns AcquireResult enum — never a bare bool.
 *
 * Web callers:
 *   - Pass current hand size for the seat (derived from their BoardPlayer equivalent).
 *   - Pass `card` as a CardDef (never null in TS — use an early guard before calling).
 *
 * On Acquired:
 *   - Mints a CardInstance (CIS).
 *   - Registers aura passive if Whenever card.
 *   - Appends action log entries.
 *
 * On SwapNeeded / Skipped:
 *   - Returns unmodified cisState + auraState.
 *
 * Mirrors CardAcquisitionService.TryAcquireCard(card, seatIndex, src).
 */
export function tryAcquireCard(
  cisState: CardInstanceState,
  auraState: AuraManagerState,
  log: ActionLog,
  card: CardDef | null,
  seatIndex: number,
  currentHandSize: number,
  src: AcquireSource,
): TryAcquireCardResult {
  assertFiniteInt(seatIndex, "tryAcquireCard seatIndex");
  assertFiniteInt(currentHandSize, "tryAcquireCard currentHandSize");

  // Null card guard — mirrors Unity Debug.LogError + return Skipped
  if (card === null) {
    console.error(`[CardAcquisitionService] null card seat=${seatIndex} src=${src}`);
    return {
      result: AcquireResult.Skipped,
      newCis: cisState,
      newAura: auraState,
      lastAcquiredInstanceId: 0,
      log,
    };
  }

  // Full hand guard — mirrors Unity handCount >= MAX_HAND_SIZE → SwapNeeded
  if (currentHandSize >= MAX_HAND_SIZE) {
    const nextLog = log.append({
      kind: "acquire.swapNeeded",
      data: { seat: seatIndex, cardName: card.name, reason: "FullHand-SwapNeeded" },
    });
    return {
      result: AcquireResult.SwapNeeded,
      newCis: cisState,
      newAura: auraState,
      lastAcquiredInstanceId: 0,
      log: nextLog,
    };
  }

  // Mint CardInstance (mirrors cis.CreateForSeat)
  const createResult: CreateForSeatResult = createForSeat(
    cisState,
    log,
    card.name,
    seatIndex,
    "Hand",
  );

  const instanceId = createResult.instanceId;
  const currentCis = createResult.newState;
  let currentLog = createResult.log;

  // Log draw + acquire instance (mirrors PlaytestLogger.LogDraw + LogAcquireInstance)
  currentLog = currentLog.append({
    kind: "acquire.draw",
    data: { seat: seatIndex, cardName: card.name, pool: card.cardType, src },
  });
  currentLog = currentLog.append({
    kind: "acquire.instance",
    data: { seat: seatIndex, instanceId, cardName: card.name },
  });

  // Register aura passive on hand entry (Whenever cards)
  const { newAura: postAura, log: postLog } = tryRegisterAuraPassive(
    auraState,
    currentLog,
    card,
    seatIndex,
    instanceId,
  );

  return {
    result: AcquireResult.Acquired,
    newCis: currentCis,
    newAura: postAura,
    lastAcquiredInstanceId: instanceId,
    log: postLog,
  };
}
